import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateHeapRecordingDTO } from '../../api/generated';
import { HeapRecordingBatch } from '../../api/queries/heapRecordingList';
import { useRecordingImage } from '../../api/queries/recordingImage';
import { useHeapMasks } from '../../api/resources/useHeapMasks';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { HeapDetectionTable } from '../Heap/HeapDetectionTable';
import { HeapMaskPreferences } from '../Heap/heapMasks';
import { DetectionRowView, HeapDetection, normalizeModelClass } from '../Heap/types';
import { getColorForClass } from '../Heap/utils';
import { HeapDetailsHeaderInfoBar } from './Heap/HeapDetailsHeaderInfoBar';
import { HeapDetailsImageCard } from './Heap/HeapDetailsImageCard';
import { HeapImageToolbar } from './Heap/HeapImageToolbar';
import { ExtraFieldsCard } from './Heap/ExtraFieldsCard';

const DETECTIONS_TABLE_MAX_HEIGHT = 400;

export interface HeapRecordingsDetailsProps {
  batch: HeapRecordingBatch;
  onHeapRecordingUpdate: (batchId: string, update: UpdateHeapRecordingDTO) => void;
  maskPreferences: HeapMaskPreferences;
  onMaskVisibilityChange: (maskId: string, visible: boolean) => void;
  onMaskOpacityChange: (maskId: string, opacity: number) => void;
}

export function HeapRecordingsDetails({
  batch,
  onHeapRecordingUpdate,
  maskPreferences,
  onMaskVisibilityChange,
  onMaskOpacityChange,
}: HeapRecordingsDetailsProps): ReactElement {
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [showAllDetections, setShowAllDetections] = useState(false);

  const selectedCam = batch.cameras[currentCameraIndex];
  const hasMultipleFrames = batch.cameras.length > 1;

  const {
    data: heapImage,
    isLoading,
    error,
  } = useRecordingImage({
    jobsiteId: batch.jobsiteId,
    recordingId: selectedCam.id,
  });

  const { entries: maskEntries, overlays } = useHeapMasks({
    masks: selectedCam.masks,
    preferences: maskPreferences,
  });

  const handleArchiveClick = () => {
    onHeapRecordingUpdate(batch.id, { archived: !batch.archived });
  };

  const handlePreviousCamera = useCallback(() => setCurrentCameraIndex((idx) => Math.max(0, idx - 1)), []);
  const handleNextCamera = useCallback(
    () => setCurrentCameraIndex((idx) => Math.min(batch.cameras.length - 1, idx + 1)),
    [batch.cameras.length]
  );

  const overallDetectionsCount = useMemo(() => batch.cameras.flatMap((cam) => cam.detections).length, [batch.cameras]);
  const detectionsInView: HeapDetection[] = useMemo(
    () => (showAllDetections ? batch.cameras.flatMap((cam) => cam.detections) : selectedCam.detections),
    [showAllDetections, batch.cameras, selectedCam.detections]
  );

  const detectionRows: DetectionRowView[] = useMemo(() => {
    const classMap = new Map<string, DetectionRowView>();
    detectionsInView.forEach((detection) => {
      detection.classes.forEach((className, index) => {
        const modelClass = normalizeModelClass(detection.sources?.[index]);
        const key = `${className}::${modelClass}`;
        const existing = classMap.get(key);
        if (existing) {
          classMap.set(key, { ...existing, count: existing.count + 1 });
          return;
        }

        classMap.set(key, {
          label: `heap.classes.${className}`,
          count: 1,
          color: getColorForClass(className),
          modelClass,
        });
      });
    });

    return Array.from(classMap.values()).sort((a, b) => b.count - a.count);
  }, [detectionsInView]);

  return (
    <Box height='calc(100% - 5rem - 1px)' display='flex' flexDirection='column' gap={1} padding={2} overflow='auto'>
      <HeapDetailsHeaderInfoBar
        overallDetectionsCount={overallDetectionsCount}
        recordedAt={batch.recordedAt}
        licensePlate={batch.licensePlate}
        archived={batch.archived}
        onArchiveClick={handleArchiveClick}
      />

      <HeapImageToolbar
        maskEntries={maskEntries}
        onMaskVisibilityChange={onMaskVisibilityChange}
        onMaskOpacityChange={onMaskOpacityChange}
        currentIndex={currentCameraIndex}
        totalCount={batch.cameras.length}
        onPrevious={handlePreviousCamera}
        onNext={handleNextCamera}
      />

      <HeapDetailsImageCard
        key={selectedCam.id}
        isLoading={isLoading}
        error={error}
        heapImage={heapImage}
        detections={selectedCam.detections}
        segmentationOverlays={overlays}
      />

      {selectedCam.extraFields && Object.keys(selectedCam.extraFields).length > 0 && (
        <ExtraFieldsCard extraFields={selectedCam.extraFields} />
      )}

      {/* Detections Table: shrinks to its rows, scrolls them once it hits the ceiling. */}
      <WrapperCardWithTitle
        title='heap.detections'
        sx={{ height: 'auto', maxHeight: `${DETECTIONS_TABLE_MAX_HEIGHT}px`, flexShrink: 0 }}
        contentSx={{ padding: 1, flex: '0 1 auto' }}
        elevation={0}
        adornment={
          hasMultipleFrames ? (
            <ShowAllDetectionsToggle checked={showAllDetections} onChange={setShowAllDetections} />
          ) : undefined
        }
      >
        <HeapDetectionTable
          rows={detectionRows}
          emptyMessage='heap.noDetections'
          classLabel='heap.class'
          countLabel='heap.count'
          modelClassLabel='heap.modelClass'
        />
      </WrapperCardWithTitle>
    </Box>
  );
}

interface ShowAllDetectionsToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ShowAllDetectionsToggle({ checked, onChange }: ShowAllDetectionsToggleProps): ReactElement {
  const { t } = useTranslation();

  return (
    <FormControlLabel
      control={<Switch size='small' checked={checked} onChange={(e) => onChange(e.target.checked)} />}
      label={<Typography variant='body2'> {t('heap.showAllDetections')}</Typography>}
    />
  );
}
